import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { breadcrumbJsonLd, SITE_URL, SITE_NAME, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT } from "@/lib/seo";
import { listApprovedReviews, submitReview } from "@/lib/reviews.functions";
import { MIN_REVIEW_SECONDS, reviewPayloadSchema } from "@/lib/reviews-schema";

const TITLE = "Parent Reviews | Smart Word Labs";
const DESCRIPTION =
  "Read verified parent reviews of Smart Word Labs online English classes, and share your own star rating and experience with Ms. Salma's lessons.";

export const Route = createFileRoute("/reviews")({
  loader: () => listApprovedReviews(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/reviews` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/reviews` }],
    scripts: [breadcrumbJsonLd([{ name: "Parent Reviews", path: "/reviews" }])],
  }),
  errorComponent: () => <ReviewsShell reviews={[]} />,
  notFoundComponent: () => <ReviewsShell reviews={[]} />,
  component: ReviewsPage,
});

function ReviewsPage() {
  const reviews = Route.useLoaderData();
  return <ReviewsShell reviews={reviews} />;
}

type Review = Awaited<ReturnType<typeof listApprovedReviews>>[number];

function Stars({ rating }: { rating: number }) {
  const { t } = useTranslation("account");
  return (
    <div className="flex items-center gap-0.5" aria-label={t("reviews.starsAriaLabel", { rating })}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          aria-hidden="true"
          className={i < rating ? "size-4 fill-accent text-accent" : "size-4 text-primary/20"}
        />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const { t } = useTranslation("account");
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label={t("reviews.starPickerAriaLabel")}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={t("reviews.starAriaLabel", { count: n })}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="rounded p-1 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Star
            className={
              n <= active ? "size-7 fill-accent text-accent" : "size-7 text-primary/25"
            }
            aria-hidden="true"
          />
        </button>
      ))}
    </div>
  );
}

function ReviewsShell({ reviews }: { reviews: Review[] }) {
  const { t } = useTranslation("account");
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <div className="mx-auto max-w-6xl px-5 pt-6">
          <Breadcrumbs trail={[{ name: "Parent Reviews", path: "/reviews" }]} />
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {t("reviews.title")}
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {t("reviews.description")}
          </p>
        </div>

        <section className="mx-auto max-w-6xl px-5 py-12">
          <ReviewForm />
        </section>

        <section className="border-t border-border bg-secondary/30 py-14">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              {t("reviews.whatParentsSay")}
            </h2>
            {reviews.length === 0 ? (
              <p className="mt-4 text-muted-foreground">
                {t("reviews.empty")}
              </p>
            ) : (
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {reviews.map((r) => (
                  <blockquote key={r.id} className="surface-card flex flex-col p-6 text-left">
                    <Stars rating={r.rating} />
                    <p className="mt-3 flex-1 text-base leading-relaxed text-primary">{r.body}</p>
                    <footer className="mt-5 border-t border-border pt-4">
                      <p className="font-semibold text-primary">{r.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {[r.child_age, r.location].filter(Boolean).join(" · ")}
                      </p>
                    </footer>
                  </blockquote>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ReviewForm() {
  const { t } = useTranslation("account");
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const loadedAt = useRef(Date.now());
  const send = useServerFn(submitReview);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("website_url") ?? "");
    if (honeypot.trim() !== "") {
      form.reset();
      setSent(true);
      return;
    }

    if (rating === 0) {
      toast.error(t("reviews.errors.chooseRating"));
      return;
    }

    const elapsedMs = Date.now() - loadedAt.current;
    if (elapsedMs < MIN_REVIEW_SECONDS * 1000) {
      toast.error(t("reviews.errors.tooFast"));
      return;
    }

    const parsed = reviewPayloadSchema.safeParse({
      name: String(data.get("name") ?? ""),
      email: String(data.get("email") ?? ""),
      location: String(data.get("location") ?? ""),
      childAge: String(data.get("childAge") ?? ""),
      rating,
      body: String(data.get("body") ?? ""),
      websiteUrl: honeypot,
      elapsedMs,
    });

    if (!parsed.success) {
      toast.error(
        parsed.error.issues[0]?.path[0] === "body"
          ? t("reviews.errors.bodyTooShort")
          : (parsed.error.issues[0]?.message ?? t("reviews.errors.checkForm")),
      );
      return;
    }

    setSubmitting(true);
    let result: Awaited<ReturnType<typeof send>> | null = null;
    try {
      result = await send({ data: parsed.data });
    } catch {
      result = null;
    }
    setSubmitting(false);

    if (!result?.ok) {
      toast.error(
        result?.reason === "too_fast"
          ? t("reviews.errors.tooFast")
          : t("reviews.errors.saveFailed"),
      );
      return;
    }

    form.reset();
    setRating(0);
    loadedAt.current = Date.now();
    setSent(true);
    toast.success(t("reviews.toasts.submitted"));
  }

  if (sent) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center gap-3 rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        <div className="rounded-full bg-accent/20 p-4">
          <Send className="size-6 text-accent-foreground" aria-hidden="true" />
        </div>
        <h2 className="font-display text-xl font-bold text-foreground">{t("reviews.sent.title")}</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          {t("reviews.sent.body")}
        </p>
        <Button variant="outline" onClick={() => setSent(false)}>
          {t("reviews.sent.writeAnother")}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
    >
      <h2 className="font-display text-2xl font-bold text-foreground">{t("reviews.form.title")}</h2>

      <div className="mt-6 space-y-5">
        <div>
          <Label>{t("reviews.form.yourRating")}</Label>
          <div className="mt-2">
            <StarPicker value={rating} onChange={setRating} />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="review-name">{t("reviews.form.yourName")}</Label>
            <Input id="review-name" name="name" required maxLength={120} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="review-email">{t("reviews.form.email")}</Label>
            <Input id="review-email" name="email" type="email" maxLength={254} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="review-childAge">{t("reviews.form.childAge")}</Label>
            <Input id="review-childAge" name="childAge" maxLength={40} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="review-location">{t("reviews.form.location")}</Label>
            <Input id="review-location" name="location" maxLength={120} className="mt-2" />
          </div>
        </div>

        <div>
          <Label htmlFor="review-body">{t("reviews.form.yourReview")}</Label>
          <Textarea
            id="review-body"
            name="body"
            required
            rows={5}
            minLength={20}
            maxLength={2000}
            placeholder={t("reviews.form.bodyPlaceholder")}
            className="mt-2"
          />
        </div>

        <div aria-hidden="true" className="hidden">
          <label htmlFor="review-website">{t("reviews.form.website")}</label>
          <input id="review-website" name="website_url" tabIndex={-1} autoComplete="off" />
        </div>

        <Button type="submit" variant="hero" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? <Loader2 className="animate-spin" /> : null}
          {t("reviews.form.submit")}
        </Button>
      </div>
    </form>
  );
}
